import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useSiteContent } from "@/context/SiteContentContext";
import MediaTile from "./MediaTile";
import { scheme as schemeByName, schemeHex } from "@/lib/schemes";

/**
 * Night Vibes — the main mixed-content showcase on the homepage.
 * Clicking a tile navigates DIRECTLY to the dedicated page for that item —
 * no intermediate modal (artists/releases/projects deserve their full canvas).
 */
export default function SceneSection({ order, scope = "home", max = 12, testId = "home-scene", channel = "scene" }) {
    const { lang } = useT();
    const { content: siteContent } = useSiteContent();
    const [features, setFeatures] = useState([]);
    const [artists, setArtists] = useState([]);
    // "radio" = the copy of scene WITH play buttons (sticky player).
    // "scene" = tiles only navigate to their link — no player, less confusing.
    const isRadio = channel === "radio";

    useEffect(() => {
        api.get("/scene", { params: { scope, active_only: true } })
            .then((r) => setFeatures(Array.isArray(r.data) ? r.data : []))
            .catch(() => setFeatures([]));
        api.get("/artists")
            .then((r) => setArtists(Array.isArray(r.data) ? r.data : []))
            .catch(() => setArtists([]));
    }, [scope]);

    const items = useMemo(() => (features || [])
        .filter((f) => {
            const ch = (f.channel || "scene").toLowerCase();
            return ch === channel || ch === "both";
        })
        .slice(0, max), [features, max, channel]);
    const [kindFilter, setKindFilter] = useState("all");
    const kinds = useMemo(() => Array.from(new Set(items.map((f) => (f.kind || "").trim().replace(/_/g, " ")).filter(Boolean))), [items]);
    const visibleItems = kindFilter === "all" ? items : items.filter((f) => (f.kind || "").trim().replace(/_/g, " ") === kindFilter);
    const artistBySlug = useMemo(() => new Map((artists || []).map((a) => [a.slug, a])), [artists]);

    if (items.length === 0) return null;

    // Radio reads the standard section keys; scene now ALSO reads the sections
    // tab keys first (title_media_player_* etc), falling back to the legacy
    // night_vibes_* values so old content keeps rendering.
    const showKicker = isRadio
        ? siteContent.section_show_kicker_radio !== "0"
        : siteContent.section_show_kicker_media_player !== "0";
    const kicker = isRadio
        ? (siteContent[`radio_kicker_${lang}`] || siteContent.radio_kicker_en || "")
        : (siteContent[`media_player_kicker_${lang}`] || siteContent.media_player_kicker_en || siteContent[`night_vibes_kicker_${lang}`] || siteContent.night_vibes_kicker_en || "");
    const showTitle = isRadio
        ? siteContent.section_show_title_radio !== "0"
        : siteContent.section_show_title_media_player !== "0";
    const title = isRadio
        ? (siteContent[`title_radio_${lang}`] || siteContent.title_radio_en || "itzkatu radio")
        : (siteContent[`title_media_player_${lang}`] || siteContent.title_media_player_en || siteContent[`night_vibes_title_${lang}`] || siteContent.night_vibes_title_en || "");
    const subtitle = isRadio
        ? (siteContent[`body_radio_${lang}`] || siteContent.body_radio_en || "")
        : (siteContent[`body_media_player_${lang}`] || siteContent.body_media_player_en || siteContent[`night_vibes_subtitle_${lang}`] || siteContent.night_vibes_subtitle_en || "");
    const showSubtitle = !!subtitle;
    // Optional freeform text block below the title (long-form intro / editorial note).
    const showText = !isRadio && siteContent.night_vibes_show_text === "1";
    const text = siteContent[`night_vibes_text_${lang}`] || siteContent.night_vibes_text_en || "";

    // Layout presets — admin picks columns + container width so the same
    // component can render as a tight 2-col editorial or a wide 4-col grid.
    // Tailwind classes are pinned so JIT doesn't strip them.
    const COLS = {
        "2": "grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-10 md:gap-x-10 md:gap-y-16",
        "3": "grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-9 md:gap-x-16 lg:gap-x-28 xl:gap-x-36 md:gap-y-14",
        "4": "grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-12",
    };
    const CONTAINERS = {
        narrow:  "max-w-3xl",
        default: "max-w-6xl",
        wide:    "max-w-7xl",
        full:    "max-w-none",
    };
    const colsKey = isRadio
        ? String(siteContent.scene_columns_radio || "3")
        : String(siteContent[`scene_columns_${scope}`] || siteContent.night_vibes_columns || "3");
    const gridCls = COLS[colsKey] || COLS["4"];
    const containerKey = siteContent[`scene_container_${scope}`] || siteContent.night_vibes_container || "default";
    const containerCls = CONTAINERS[containerKey] || CONTAINERS.default;

    // Section scheme — each channel can pick its own color universe.
    const sectionSchemeName = (isRadio ? siteContent.section_scheme_radio : siteContent.section_scheme_media_player) || "";
    const sSch = sectionSchemeName ? schemeByName(sectionSchemeName) : null;
    const sHex = sectionSchemeName ? schemeHex(sectionSchemeName) : null;
    const sectionCls = sSch ? `${sSch.bg} ${sSch.text}` : "";
    const sectionStyle = sHex ? {
        "--sc-bg": sHex.bg,
        "--sc-text": sHex.text,
        "--sc-subtext": sHex.subtext,
        "--sc-accent": sHex.accent,
        "--sc-accent2": sHex.accent2,
        "--sc-border": sHex.border,
        "--sc-card": sHex.card,
    } : {};

    // Resolve the destination for a scene feature.
    // Priority: cta_url (explicit "open" link) → first collaborator's artist page → credit_url.
    const featureHref = (f) => {
        if (f.cta_url && f.cta_url.trim()) return f.cta_url.trim();
        const first = (f.collaborator_slugs || [])[0];
        if (first) return `/artists/${first}`;
        if (f.credit_url && f.credit_url.trim()) return f.credit_url.trim();
        return "";
    };

    return (
        <section
            data-testid={testId}
            style={{ order, ...sectionStyle }}
            className={`${sectionCls} px-4 md:px-8 pb-6 md:pb-8`}
        >
            <div className={`${containerCls} mx-auto border-t border-current/10 pt-6 md:pt-8`}>
            {(showKicker && kicker) || (showTitle && title) || (showSubtitle && subtitle) || (showText && text) ? (
                <div className="mb-8 md:mb-10 space-y-3 text-left">
                    {showKicker && kicker && (
                        <div className="font-mono text-xs md:text-sm uppercase tracking-[0.35em] text-[color:var(--sc-accent)]">
                            {kicker}
                        </div>
                    )}
                    {showTitle && title && (
                        <h2 className="itz-preserve itz-display font-heading font-black uppercase tracking-tighter leading-[0.95]">
                            {title.replace(/\.$/, "")}<span className="text-[color:var(--sc-accent)]">.</span>
                        </h2>
                    )}
                    {showSubtitle && subtitle && (
                        <p className="font-body text-base md:text-lg leading-relaxed opacity-80 whitespace-pre-line max-w-2xl">
                            {subtitle}
                        </p>
                    )}
                    {showText && text && (
                        <p className="font-body text-base md:text-lg leading-relaxed opacity-80 whitespace-pre-line max-w-2xl">
                            {text}
                        </p>
                    )}
                </div>
            ) : null}
            {kinds.length > 1 && (
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-6" data-testid={`${testId}-filters`}>
                    {["all", ...kinds].map((k) => (
                        <button
                            key={k}
                            onClick={() => setKindFilter(k)}
                            data-testid={`${testId}-filter-${k}`}
                            className={`font-mono text-[11px] lowercase tracking-[0.2em] transition-colors ${kindFilter === k ? "text-[color:var(--sc-accent)]" : "opacity-60 hover:opacity-100"}`}
                        >
                            #{k}
                        </button>
                    ))}
                </div>
            )}

            <div className={`grid ${gridCls}`}>
                {visibleItems.map((f, i) => {
                    const collab = (f.collaborator_slugs || [])
                        .map((s) => artistBySlug.get(s))
                        .filter(Boolean);
                    const primary = collab[0];
                    // Per-item shape (admin can pick square / tall / wide, or leave
                    // it on "auto"). "wide" tiles span 2 grid columns so they read
                    // as a proper wide banner inside the 4-column media player grid.
                    const aspect = (f.aspect && f.aspect !== "")
                        ? f.aspect
                        : (i % 5 === 3 ? "tall" : "square");
                    const spanCls = aspect === "wide" ? "col-span-2" : "";
                    const dark = ["release", "event", "party", "collab"].includes((f.kind || "").toLowerCase());
                    // Title uses the feature's own title FIRST (editable in admin,
                    // usually imported from the source), then falls back. The subtitle
                    // shows the "type" (music video, street art, etc.) — italic under
                    // the title in the caption. Matches the Ksawery-style layout.
                    const tileTitle = (f.title && f.title.trim())
                        || (primary && primary.name)
                        || f.credit_label
                        || (f.kind || "").replace(/_/g, " ");
                    const tileCaption = (f.subtitle && f.subtitle.trim())
                        || (f.kind || "").replace(/_/g, " ");
                    // Click action: admin picks per item; default = radio plays, scene links.
                    const clickAction = f.click_action || (isRadio && f.embed_url ? "play" : "");
                    return (
                        <div key={f.id} className={spanCls}>
                        <MediaTile
                            testId={`scene-tile-${f.id}`}
                            title={tileTitle}
                            caption={tileCaption}
                            image_url={f.hero_image_url || f.poster_image_url || (primary && primary.image_url) || ""}
                            image_url_alt={primary && primary.image_url_alt}
                            embed_url={f.embed_url}
                            render_preset={f.render_preset}
                            render_asset_url={f.render_asset_url}
                            auto_preset
                            scheme={f.scheme}
                            seed={f.id || f.title}
                            aspect={aspect}
                            dark={dark && !!(f.hero_image_url || (primary && primary.image_url))}
                            href={featureHref(f)}
                            clickAction={clickAction}
                            playHref={primary ? `/artists/${primary.slug}` : ""}
                            playSubtitle={tileTitle}
                        />
                        </div>
                    );
                })}
            </div>
            <div className="mt-8 flex justify-end">
                <Link
                    to={siteContent[`${isRadio ? "radio" : "scene"}_see_all_url`] || "/artists"}
                    data-testid={`${testId}-see-all`}
                    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-white hover:text-[color:var(--sc-accent)] transition-colors"
                >
                    {lang === "es" ? "ver todo" : "see all"} <ArrowRight size={14} />
                </Link>
            </div>
            </div>
        </section>
    );
}
